import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { useLocation } from "react-router-dom";
import { useLang } from "@/lib/i18n";
import { useSite } from "@/lib/site";
import { canonicalOrigin } from "@/lib/canonical";
import HEROES from "@/lib/pageHeroes.json";

const absolute = (url, origin) => {
  if (!url) return "";
  if (/^https?:\/\//.test(url)) return url;
  return `${origin}${url.startsWith("/") ? "" : "/"}${url}`;
};

// JSON-LD is injected directly: react-helmet-async does not reliably emit inline
// script bodies on the client, and Google accepts structured data anywhere in the document.
function useJsonLd(data) {
  useEffect(() => {
    const id = "page-json-ld";
    const existing = document.getElementById(id);
    if (!data) {
      if (existing) existing.remove();
      return undefined;
    }
    const el = existing || document.createElement("script");
    el.id = id;
    el.type = "application/ld+json";
    el.textContent = JSON.stringify(data);
    if (!existing) document.head.appendChild(el);
    return undefined;
  }, [data && JSON.stringify(data)]);
}

/**
 * Per-page head tags. `page` looks up the CMS override in settings.page_seo;
 * explicit title/description/image props win over the CMS, which wins over site defaults.
 */
export default function Seo({ page, title, description, image, type = "website", noindex = false, jsonLd = null }) {
  const { lang, L } = useLang();
  const site = useSite();
  const { pathname } = useLocation();
  useJsonLd(jsonLd);
  const s = site?.settings || {};

  const entry = (s.page_seo || []).find((p) => p.page === page) || {};
  const siteName = L(s.site_name) || "Al Qalaa International Group";
  const suffix = L(s.seo_title_suffix) || siteName;
  // links should always point at the one canonical host, whichever domain the visitor used
  const origin = canonicalOrigin(s) || window.location.origin;

  const pageTitle = title || L(entry.title);
  const fullTitle = pageTitle ? `${pageTitle} — ${suffix}` : suffix;
  const desc = description || L(entry.description) || L(s.seo_description);
  // the page's own hero carries the story better than a generic cover
  const heroFallback = page === "home" ? (s.hero_slides || []).find((sl) => sl.image_url)?.image_url : HEROES[page];
  const ogImage = absolute(image || entry.image || heroFallback || s.seo_image, origin);
  const url = `${origin}${pathname}`;

  const meta = [
    { name: "description", content: desc },
    { name: "robots", content: noindex ? "noindex, follow" : "index, follow" },
    { property: "og:type", content: type },
    { property: "og:site_name", content: siteName },
    { property: "og:title", content: fullTitle },
    { property: "og:description", content: desc },
    { property: "og:url", content: url },
    { property: "og:locale", content: lang === "ar" ? "ar_AE" : "en_AE" },
    { name: "twitter:card", content: ogImage ? "summary_large_image" : "summary" },
    { name: "twitter:title", content: fullTitle },
    { name: "twitter:description", content: desc },
  ];

  if (s.seo_keywords) meta.push({ name: "keywords", content: s.seo_keywords });
  if (ogImage) {
    meta.push(
      { property: "og:image", content: ogImage },
      { property: "og:image:width", content: "1200" },
      { property: "og:image:height", content: "630" },
      { property: "og:image:alt", content: fullTitle },
      { name: "twitter:image", content: ogImage }
    );
  }

  return (
    <Helmet
      htmlAttributes={{ lang, dir: lang === "ar" ? "rtl" : "ltr" }}
      title={fullTitle}
      meta={meta.filter((m) => m.content)}
      link={[{ rel: "canonical", href: url }]}
    />
  );
}

export function organizationJsonLd(settings, L) {
  const origin = canonicalOrigin(settings) || window.location.origin;
  return {
    "@context": "https://schema.org",
    "@type": "Organization",
    name: L(settings?.site_name) || "Al Qalaa International Group",
    url: origin,
    logo: absolute(settings?.logo_url, origin),
    description: L(settings?.seo_description),
    email: settings?.email || undefined,
    telephone: settings?.phone || undefined,
    sameAs: settings?.linkedin ? [settings.linkedin] : undefined,
    address: settings?.address
      ? {
          "@type": "PostalAddress",
          streetAddress: settings.address,
          addressLocality: "Abu Dhabi",
          addressCountry: "AE",
        }
      : undefined,
  };
}

export function articleJsonLd(post, settings, L) {
  const origin = canonicalOrigin(settings) || window.location.origin;
  return {
    "@context": "https://schema.org",
    "@type": "NewsArticle",
    headline: L(post.title),
    description: L(post.excerpt),
    image: absolute(post.image, origin),
    datePublished: post.date,
    author: { "@type": "Organization", name: L(settings?.site_name) },
    publisher: {
      "@type": "Organization",
      name: L(settings?.site_name),
      logo: { "@type": "ImageObject", url: absolute(settings?.logo_url, origin) },
    },
    mainEntityOfPage: `${origin}/news/${post.slug}`,
  };
}
